import {
  AbsoluteTime,
  assertUnreachable,
  Challenge,
  ChallengeResponse,
  HttpStatusCode,
  opEmptySuccess,
  TalerErrorCode,
  TanChannel,
  TranslatedString,
} from "@gnu-taler/taler-util";
import { dummyHttpResponse } from "@gnu-taler/taler-util/http";
import {
  AsyncAction,
  AsyncButton,
  ShowInputErrorLabel,
  Time,
  undefinedIfEmpty,
  useAsyncAction,
  useBankCoreApiContext,
  useNotificationContext,
  useNotifiedOperation,
  useTranslationContext,
} from "@gnu-taler/web-util/browser";
import { ComponentChildren, Fragment, h, VNode } from "preact";
import { useCallback, useEffect, useRef, useState } from "preact/hooks";
import { useBankChallengeHandlerContext } from "../context/challenge.js";
import { AuthAlert } from "./AuthForm.js";
import { OperationError } from "../components/OperationError.js";
import { doAutoFocus } from "./PaytoWireTransferForm.js";

export interface Props {
  onCompleted: AsyncAction<[challenges: string[]]>;
  username: string;
  onCancel(): void;
  description: TranslatedString;
  currentChallenge: ChallengeResponse;
  focus?: boolean;
}

function SolveChallenge({
  challenge,
  onCancel,
  onSolved,
  username,
  expiration,
  onTerminal,
}: {
  onCancel: () => void;
  challenge: Challenge;
  expiration: AbsoluteTime;
  onSolved: () => void;
  username: string;
  onTerminal: (manual?: boolean) => Promise<boolean>;
}): VNode {
  const { i18n } = useTranslationContext();
  const [tanCode, setTanCode] = useState<string>();
  const [submitted, setSubmitted] = useState(false);
  const [verificationError, setVerificationError] =
    useState<TranslatedString>();
  const {
    lib: { bank: api },
  } = useBankCoreApiContext();
  const [showExpired, setExpired] = useState(
    expiration !== undefined && AbsoluteTime.isExpired(expiration),
  );
  const [manualRestart, setManualRestart] = useState(false);
  const terminalHandled = useRef(false);

  const handleTerminal = useCallback(
    async (manual = false): Promise<void> => {
      if (!manual && terminalHandled.current) return;
      if (!manual) terminalHandled.current = true;
      const restarted = await onTerminal(manual);
      if (!restarted) setManualRestart(true);
    },
    [onTerminal],
  );

  const errors = undefinedIfEmpty({
    code: !tanCode ? i18n.str`Required` : undefined,
  });

  useEffect(() => {
    if (showExpired) {
      void handleTerminal();
      return;
    }
    const remain = AbsoluteTime.remaining(expiration).d_ms;
    if (remain === "forever") return;
    const handler = setTimeout(() => {
      setExpired(true);
      void handleTerminal();
    }, remain);
    return () => {
      clearTimeout(handler);
    };
  }, [expiration, handleTerminal, showExpired]);

  const doVerification = useAsyncAction<
    Awaited<ReturnType<typeof api.confirmChallenge>>,
    [string]
  >(
    (ct, tan: string) =>
      api.confirmChallenge(username, challenge.challenge_id, { tan }),
    {
      onResult: (result) => {
        if (result.type === "ok") {
          setVerificationError(undefined);
          onSolved();
          return;
        }
        let message: TranslatedString;
        switch (result.case) {
          case TalerErrorCode.BANK_TRANSACTION_NOT_FOUND:
            message = i18n.str`Unknown challenge.`;
            break;
          case HttpStatusCode.Unauthorized:
            message = i18n.str`Failed to validate the verification code.`;
            break;
          case HttpStatusCode.TooManyRequests:
            message = i18n.str`Too many challenges are active. Wait or confirm an existing challenge.`;
            break;
          case TalerErrorCode.BANK_TAN_CHALLENGE_FAILED:
            message = i18n.str`The verification code is incorrect.`;
            break;
          case TalerErrorCode.BANK_TAN_CHALLENGE_EXPIRED:
            setExpired(true);
            void handleTerminal();
            message = i18n.str`The verification code has expired.`;
            break;
          default:
            assertUnreachable(result);
        }
        setVerificationError(message);
      },
      onError: () => {
        setVerificationError(
          i18n.str`Could not reach the bank. Check your connection and try again.`,
        );
      },
    },
  );

  return (
    <Fragment>
      <div class="grid grid-cols-1 gap-x-8 gap-y-8 pt-6 md:grid-cols-3 bg-gray-100 my-4 px-4 pb-4 rounded-lg">
        <div class="px-4 sm:px-0">
          <h2 class="text-base font-semibold leading-7 text-onBackground">
            <span
              class="text-sm text-onBackground font-semibold leading-6 "
              id="dialog-title"
            >
              <i18n.Translate>
                Submit the transmitted code number.
              </i18n.Translate>
            </span>
          </h2>
          <p class="mt-2 text-sm text-gray-500">
            {(function (c): VNode {
              switch (c.tan_channel) {
                case TanChannel.EMAIL:
                  return (
                    <i18n.Translate>
                      The verification code sent to the email address starting
                      with <b>"{c.tan_info}"</b>
                    </i18n.Translate>
                  );
                case TanChannel.SMS:
                  return (
                    <i18n.Translate>
                      The verification code sent to the phone number ending with{" "}
                      <b>"{c.tan_info}"</b>
                    </i18n.Translate>
                  );
              }
            })(challenge)}
          </p>
        </div>

        <div class="bg-white shadow-sm ring-1 ring-onBackground/5 sm:rounded-xl md:col-span-2">
          <div class="px-4 mt-4 ">
            <form
              class="space-y-6"
              noValidate
              onSubmit={(e) => {
                e.preventDefault();
                setSubmitted(true);
                if (!tanCode || showExpired || doVerification.running) return;
                setVerificationError(undefined);
                void doVerification.run(tanCode);
              }}
              autoCapitalize="none"
              autoCorrect="off"
            >
              {verificationError ? (
                <AuthAlert type="error">{verificationError}</AuthAlert>
              ) : undefined}
              <div>
                <label
                  htmlFor={`tan-${challenge.challenge_id}`}
                  class="block text-sm font-medium leading-6 text-onBackground"
                >
                  <i18n.Translate>Code</i18n.Translate>
                </label>
                <div class="mt-2">
                  <input
                    ref={doAutoFocus}
                    type="text"
                    name="authentication-code"
                    id={`tan-${challenge.challenge_id}`}
                    class="block w-full disabled:bg-gray-200 rounded-md border-0 py-1.5 text-onBackground shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                    value={tanCode ?? ""}
                    enterkeyhint="done"
                    placeholder="T-12345678"
                    autocomplete="one-time-code"
                    title={i18n.str`Authentication code`}
                    required
                    aria-invalid={
                      (submitted || tanCode !== undefined) && !!errors
                    }
                    aria-describedby={
                      (submitted || tanCode !== undefined) && errors
                        ? `tan-${challenge.challenge_id}-error`
                        : undefined
                    }
                    onInput={(e): void => {
                      setTanCode(e.currentTarget.value);
                    }}
                  />
                  <div id={`tan-${challenge.challenge_id}-error`}>
                    <ShowInputErrorLabel
                      message={errors?.code}
                      isDirty={submitted || tanCode !== undefined}
                    />
                  </div>
                </div>
              </div>
              {expiration.t_ms === "never" ? undefined : (
                <p class="text-gray-400 text-sm mt-2">
                  <i18n.Translate>
                    It will expire at{" "}
                    <Time format="HH:mm" timestamp={expiration} />
                  </i18n.Translate>
                </p>
              )}
              {showExpired ? (
                <p class="text-sm">
                  <i18n.Translate>
                    This challenge is terminal and cannot be retransmitted.
                  </i18n.Translate>
                </p>
              ) : undefined}

              {manualRestart && (
                <button
                  type="button"
                  class="mt-3 rounded-md bg-primary px-3 py-2 text-sm font-semibold text-onPrimary"
                  onClick={() => void handleTerminal(true)}
                >
                  <i18n.Translate>Restart authentication</i18n.Translate>
                </button>
              )}

              <div class="mt-6 mb-4 flex justify-between">
                <button
                  type="button"
                  name="cancel"
                  class="text-sm font-semibold leading-6 text-onBackground"
                  onClick={onCancel}
                >
                  <i18n.Translate>Back</i18n.Translate>
                </button>

                <button
                  type="submit"
                  name="verify"
                  class="disabled:opacity-50 disabled:cursor-default cursor-pointer rounded-md bg-primary px-3 py-2 text-sm font-semibold text-onPrimary shadow-sm hover:bg-primaryHover hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                  disabled={showExpired || doVerification.running}
                  aria-busy={doVerification.running}
                >
                  {doVerification.running ? (
                    <i18n.Translate>Verifying…</i18n.Translate>
                  ) : (
                    <i18n.Translate>Verify</i18n.Translate>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Fragment>
  );
}

export function SolveChallengeDialog({
  children,
}: {
  children: ComponentChildren;
}): VNode {
  const mfa = useBankChallengeHandlerContext();

  return (
    <Fragment>
      <dialog
        id="dialog"
        tabIndex={-1}
        // open
        open={mfa.pending !== undefined}
        aria-labelledby="dialog-title"
        class="z-30 fixed inset-0 size-auto max-h-none max-w-none overflow-y-auto bg-transparent backdrop:bg-transparent"
      >
        <div class="fixed inset-0 bg-gray-500/75 transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in dark:bg-onBackground/50"></div>
        <div class="flex min-h-full items-end justify-center p-4 text-center focus:outline-none sm:items-center sm:p-0 w-800">
          <div class="z-40 max-w-7xl text-left">
            {!mfa.pending ? undefined : (
              <SolveMFAChallenges
                key={`${mfa.pending.regenerationCount}:${mfa.pending.requirement.challenges.map((challenge) => challenge.challenge_id).join(":")}`}
                currentChallenge={mfa.pending.requirement}
                description={mfa.pending.title}
                onCancel={mfa.cancel}
                username={mfa.pending.username}
                onCompleted={mfa.pending.retry}
                focus
              />
            )}
          </div>
        </div>
      </dialog>
      <Fragment key="children">{children}</Fragment>
    </Fragment>
  );
}

function SolveMFAChallenges({
  currentChallenge,
  username,
  focus,
  description,
  onCompleted,
  onCancel,
}: Props): VNode {
  const { i18n } = useTranslationContext();
  // Translators: Button that finishes multi-factor authentication.
  const completeLabel = i18n.str`Complete`;
  const mfa = useBankChallengeHandlerContext();

  const [solved, setSolved] = useState<string[]>([]);
  const [selected, setSelected] = useState<{
    ch: Challenge;
    expiration: AbsoluteTime;
  }>();
  const { showError } = useNotificationContext();

  const {
    lib: { bank: api },
  } = useBankCoreApiContext();
  // FIXME: we should save here also the expiration of the
  // tan channel to be used when the user press "i have the code"
  const [retransmission, setRetransmission] = useState<
    Record<string, AbsoluteTime | undefined>
  >({});
  const autoSent = useRef(new Set<string>());
  const [clock, setClock] = useState(0);

  // i18n.str`send MFA challenge`,
  const sendMessage = useNotifiedOperation<
    Awaited<ReturnType<typeof api.sendChallenge>>,
    [Challenge]
  >((ct, ch: Challenge) => api.sendChallenge(username, ch.challenge_id), {
    onSuccess: (success, ch) => {
      const earliestRetransmission = success.earliest_retransmission;
      if (earliestRetransmission) {
        setRetransmission((previous) => ({
          ...previous,
          [ch.challenge_id]: AbsoluteTime.fromProtocolTimestamp(
            earliestRetransmission,
          ),
        }));
      }
      setSelected({
        ch,
        expiration: !success.solve_expiration
          ? AbsoluteTime.never()
          : AbsoluteTime.fromProtocolTimestamp(success.solve_expiration),
      });
    },
    onFail: showError(i18n.str`Failed to start the challenge.`, (fail) => {
      switch (fail.case) {
        case HttpStatusCode.Unauthorized:
          return i18n.str`Unable to send the verification code.`;
        case HttpStatusCode.Forbidden:
          return i18n.str`The request was valid, but the server is refusing action.`;
        case HttpStatusCode.NotFound:
          return i18n.str`The backend is not aware of the specified MFA challenge.`;
        case HttpStatusCode.TooManyRequests:
          return i18n.str`It is too early to request another transmission of the challenge.`;
        case TalerErrorCode.BANK_TAN_CHANNEL_SCRIPT_FAILED:
          return i18n.str`Code transmission failed.`;
        default:
          assertUnreachable(fail);
      }
    }),
  });
  const sendMessageRunning = sendMessage.running;
  const sendMessageRun = sendMessage.run;

  // i18n.str`select challenge`,
  const selectChallenge = useNotifiedOperation(async (ct, ch: Challenge) => {
    setSelected({
      ch,
      expiration: AbsoluteTime.never(),
    });
    return opEmptySuccess(dummyHttpResponse);
  });

  const nextAndChallenge = currentChallenge.combi_and
    ? currentChallenge.challenges.find(
        ({ challenge_id }) => !solved.includes(challenge_id),
      )
    : undefined;

  useEffect(() => {
    if (
      !nextAndChallenge ||
      selected ||
      sendMessageRunning ||
      autoSent.current.has(nextAndChallenge.challenge_id)
    ) {
      return;
    }
    autoSent.current.add(nextAndChallenge.challenge_id);
    void sendMessageRun(nextAndChallenge);
  }, [nextAndChallenge, selected, sendMessageRun, sendMessageRunning]);

  useEffect(() => {
    const waits = Object.values(retransmission)
      .map((time) => (time ? AbsoluteTime.remaining(time).d_ms : "forever"))
      .filter((time): time is number => time !== "forever" && time > 0);
    if (waits.length === 0) return;
    const timer = setTimeout(
      () => setClock((value) => value + 1),
      Math.min(...waits),
    );
    return () => clearTimeout(timer);
  }, [retransmission, clock]);

  if (selected) {
    return (
      <SolveChallenge
        onCancel={() => setSelected(undefined)}
        challenge={selected.ch}
        expiration={selected.expiration}
        username={username}
        onTerminal={(manual) => mfa.regenerate(manual)}
        onSolved={async () => {
          setSelected(undefined);
          const total = [...solved, selected.ch.challenge_id];
          const enough = currentChallenge.combi_and
            ? total.length === currentChallenge.challenges.length
            : total.length > 0;

          if (enough) {
            setSolved(total);
            await onCompleted.run(total);
          } else {
            setSolved(total);
          }
        }}
      />
    );
  }

  const currentSolved = currentChallenge.challenges.filter(
    ({ challenge_id }) => solved.indexOf(challenge_id) !== -1,
  );
  const hasSolvedEnough = currentChallenge.combi_and
    ? currentSolved.length === currentChallenge.challenges.length
    : currentSolved.length > 0;

  return (
    <Fragment>
      <div class="grid grid-cols-1 gap-x-8 gap-y-8 pt-6 md:grid-cols-3 bg-gray-100 my-4 px-4 pb-4 rounded-lg">
        <div class="px-4 px-0">
          <h2 class="text-base font-semibold leading-7 text-onBackground">
            <span
              class="text-sm text-onBackground font-semibold leading-6 "
              id="dialog-title"
            >
              <i18n.Translate>
                Multi-factor authentication required
              </i18n.Translate>
            </span>
          </h2>
          <p class="mt-2 text-sm text-gray-500">
            <i18n.Translate>
              This operation is protected with second factor authentication. In
              order to complete it we need to verify your identity using the
              authentication channel you provided.
            </i18n.Translate>
          </p>
        </div>

        <div class="bg-white shadow-sm ring-1 ring-onBackground/5 rounded-xl md:col-span-2">
          <div class="px-4 mt-4 ">
            <div class="w-full">
              <div class="border-gray-100">
                <h2 class="text-base font-semibold leading-10 text-onBackground">
                  <span class=" text-onBackground font-semibold leading-6 ">
                    {description}
                  </span>
                </h2>
              </div>
            </div>

            <h2 class="text-base  leading-7 text-onBackground ">
              <span class="text-sm leading-6">
                {currentChallenge.challenges.length === 1 ? (
                  <i18n.Translate>
                    The next challenge needs to be completed to confirm the
                    operation.
                  </i18n.Translate>
                ) : currentChallenge.combi_and ? (
                  <i18n.Translate>
                    All the next challenges need to be completed to confirm the
                    operation.
                  </i18n.Translate>
                ) : (
                  <i18n.Translate>
                    One of the next challenges needs to be completed to confirm
                    the operation.
                  </i18n.Translate>
                )}
              </span>
            </h2>
            {currentChallenge.challenges.map((challenge) => {
              const time =
                retransmission[challenge.challenge_id] ?? AbsoluteTime.now();
              const alreadySent = !AbsoluteTime.isExpired(time);
              const noNeedToComplete =
                hasSolvedEnough ||
                solved.indexOf(challenge.challenge_id) !== -1 ||
                (currentChallenge.combi_and &&
                  nextAndChallenge?.challenge_id !== challenge.challenge_id);

              return (
                <div
                  key={challenge.challenge_id}
                  class="rounded-xl border px-2 my-2"
                >
                  <dl class="divide-y divide-gray-100">
                    <div class="px-4 py-2 sm:grid  sm:gap-4 sm:px-0">
                      <dt class="text-sm font-medium leading-6 text-onBackground">
                        {((ch: TanChannel): VNode => {
                          switch (ch) {
                            case TanChannel.SMS:
                              return (
                                <i18n.Translate>
                                  To a phone ending with "{challenge.tan_info}"
                                </i18n.Translate>
                              );
                            case TanChannel.EMAIL:
                              return (
                                <i18n.Translate>
                                  To an email starting with "
                                  {challenge.tan_info}"
                                </i18n.Translate>
                              );
                          }
                        })(challenge.tan_channel)}
                      </dt>
                      <dd class="mt-1 text-sm leading-6 text-gray-700  sm:mt-0">
                        <div class="flex justify-between">
                          <AsyncButton
                            name="cancel"
                            class="text-sm font-semibold leading-6 text-onBackground"
                            disabled={noNeedToComplete}
                            onClick={() => selectChallenge.run(challenge)}
                          >
                            <i18n.Translate>I have a code</i18n.Translate>
                          </AsyncButton>

                          <AsyncButton
                            submit
                            name="send again"
                            class="disabled:opacity-50 disabled:cursor-default cursor-pointer rounded-md bg-primary px-3 py-2 text-sm font-semibold text-onPrimary shadow-sm hover:bg-primaryHover hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                            disabled={alreadySent || noNeedToComplete}
                            onClick={() => sendMessage.run(challenge)}
                          >
                            <i18n.Translate>Send me a message</i18n.Translate>
                          </AsyncButton>
                        </div>
                      </dd>
                      {alreadySent && time.t_ms !== "never" ? (
                        <p class="text-sm text-gray-600">
                          <i18n.Translate>
                            You have to wait until{" "}
                            <Time format="HH:mm" timestamp={time} /> to send a
                            new code.
                          </i18n.Translate>
                        </p>
                      ) : undefined}
                    </div>
                  </dl>
                </div>
              );
            })}

            <OperationError class="mt-4" />
            <div class="mt-6 mb-4 flex justify-between">
              <button
                type="button"
                name="cancel"
                class="text-sm font-semibold leading-6 text-onBackground"
                onClick={onCancel}
              >
                <i18n.Translate>Cancel</i18n.Translate>
              </button>

              <AsyncButton
                submit
                name="send again"
                class="disabled:opacity-50 disabled:cursor-default cursor-pointer rounded-md bg-primary px-3 py-2 text-sm font-semibold text-onPrimary shadow-sm hover:bg-primaryHover hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                disabled={!hasSolvedEnough}
                onClick={() => onCompleted.run(solved)}
              >
                {completeLabel}
              </AsyncButton>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
}
